import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Copy } from "lucide-react";
import { streamPost } from "@/lib/api";

const PRESETS = [
  {
    id: "migliora",
    label: "Migliora un testo",
    instruction:
      "Migliora e rendi più evocativo questo testo per il sito della gelateria, mantenendo un tono elegante e autentico (max 60 parole):",
  },
  {
    id: "gusto",
    label: "Descrivi un gusto",
    instruction:
      "Scrivi la descrizione (max 40 parole) e il paragrafo 'Perché lo amano' (max 25 parole) per questo gusto di gelato artigianale:",
  },
  {
    id: "ferie",
    label: "Annuncio chiusura",
    instruction:
      "Scrivi un breve annuncio cordiale (max 30 parole) per questa chiusura straordinaria della gelateria:",
  },
  {
    id: "social",
    label: "Post social",
    instruction: "Scrivi un post Instagram breve e invitante (max 50 parole, con 3 hashtag) per la gelateria su:",
  },
];

export default function AssistenteAI() {
  const [preset, setPreset] = useState(PRESETS[0]);
  const [text, setText] = useState("");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!text.trim()) {
      toast.error("Scrivi prima il contenuto o l'argomento");
      return;
    }
    setOutput("");
    setLoading(true);
    try {
      await streamPost("/admin/ai/polish", { instruction: preset.instruction, text }, (t) =>
        setOutput((o) => o + t)
      );
    } catch {
      toast.error("Errore AI: riprova tra poco");
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(output);
    toast.success("Testo copiato negli appunti");
  };

  return (
    <div data-testid="admin-ai-assistant">
      <h1 className="font-display text-3xl font-semibold text-ink sm:text-4xl">Assistente AI</h1>
      <p className="mt-2 text-sm text-stone-500">
        L'intelligenza artificiale ti aiuta a scrivere testi per il sito, annunci e post social.
      </p>

      <div className="mt-8 flex flex-wrap gap-2">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            data-testid={`ai-preset-${p.id}`}
            onClick={() => setPreset(p)}
            className={`rounded-full px-5 py-2.5 font-mono-alt text-[11px] uppercase tracking-[0.18em] transition-colors ${
              preset.id === p.id ? "bg-bordeaux text-ivory" : "border border-bordeaux/25 text-bordeaux hover:bg-bordeaux/5"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <textarea
        data-testid="ai-input"
        rows={4}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Scrivi qui il testo da migliorare o l'argomento (es. nuovo gusto amarena, chiusura dal 10 al 24 agosto…)"
        className="mt-5 w-full rounded-2xl border border-bordeaux/20 bg-white px-5 py-4 text-sm outline-none focus:border-bordeaux"
      />

      <button
        data-testid="ai-generate-button"
        onClick={generate}
        disabled={loading}
        className="mt-5 inline-flex items-center gap-2 rounded-full bg-bordeaux px-8 py-4 font-mono-alt text-xs uppercase tracking-[0.22em] text-ivory transition-transform duration-300 hover:scale-[1.03] disabled:opacity-60"
      >
        <Sparkles className="h-4 w-4" /> {loading ? "Generazione in corso…" : "Genera con AI"}
      </button>

      {(output || loading) && (
        <div className="mt-6 rounded-2xl border border-bordeaux/15 bg-white p-6 sm:p-8">
          <div className="mb-3 flex items-center justify-between">
            <span className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-stone-400">
              Bozza generata — da rivedere
            </span>
            {output && (
              <button
                data-testid="ai-copy-button"
                onClick={copy}
                className="inline-flex items-center gap-1.5 rounded-full border border-bordeaux/25 px-4 py-1.5 font-mono-alt text-[10px] uppercase tracking-[0.18em] text-bordeaux transition-colors hover:bg-bordeaux hover:text-ivory"
              >
                <Copy className="h-3.5 w-3.5" /> Copia
              </button>
            )}
          </div>
          <p data-testid="ai-output" className="whitespace-pre-wrap font-display text-xl leading-relaxed text-stone-700">
            {output || <span className="animate-pulse text-stone-400">…</span>}
          </p>
        </div>
      )}
    </div>
  );
}
